import { signIn, signOut, restoreSession, AuthError } from './auth.js';
import { loadClasses } from './loadclasses.js';
import { loadStudy } from './loadstudy.js';
import { CLASSES, replaceClasses } from './classes.js';
import { validateAudioFile } from './validate.js';
import { uploadMemo, describeUploadError } from './upload.js';
import { listMemos, recordMemo } from './memos.js';
import * as ui from './ui.js';
import * as screens from './screens.js';
import * as timerscreen from './timerscreen.js';
import * as sessions from './sessions.js';
import { clientIdFor, createCodeConsent, createTokenStore, isConfigured, loadGis } from './gauth.js';
import { GoogleNotConnected, connectGoogle, disconnectGoogle, googleAccessToken } from './googleapi.js';

let config = null;
let session = null;
let username = null;
let pendingCourse = null;

/**
 * Google is a second consent, on top of the Cognito sign-in, and this is all
 * of it that lives outside gauth.js and googleapi.js.
 *
 * The access token sits in `googleTokens`, in this tab, and is written
 * nowhere. What changed on 2026-08-17 is where the *durable* half lives: the
 * backend holds the refresh token, so a home-screen app on iOS -- which has
 * its own cookie container and therefore no Google session to renew against
 * -- stops asking for a full login on every launch.
 *
 * There is no "have I connected before" bit any more. The backend knows, and
 * says so distinguishably: `GoogleNotConnected` means press Connect.
 */
const googleTokens = createTokenStore();
let codeConsent = null;

/**
 * A calendar token, asking the student only when there is no other way.
 *
 * Three steps, cheapest first: the token already in this tab, then one the
 * backend can mint from the grant on record, and only then a consent dialog.
 * The middle step is the whole feature -- it is what a reload costs now.
 */
async function googleToken(interactive) {
  const live = googleTokens.get();
  if (live) return live;

  if (!isConfigured(config)) throw new Error('Google Calendar is not set up for this site yet.');

  try {
    return googleTokens.set(await googleAccessToken(config, session));
  } catch (error) {
    if (!(error instanceof GoogleNotConnected) || !interactive) throw error;
  }

  // Nothing on record, and the student asked for it. This is the only path
  // that shows a dialog, and it is the only path that ever should.
  if (!codeConsent) codeConsent = createCodeConsent(clientIdFor(config), await loadGis());
  const code = await codeConsent.requestCode();
  const token = googleTokens.set(await connectGoogle(config, session, code));
  if (!token) throw new Error('Google did not return an access token.');
  return token;
}

const calendarAccess = {
  get configured() {
    return isConfigured(config);
  },
  get connected() {
    return googleTokens.peek() !== null;
  },
  getToken: googleToken,
  /**
   * The calendar back without a dialog.
   *
   * Now a single call to our own backend rather than a hopeful ask of Google,
   * which is why it works in a home-screen app where the old way could not.
   */
  async reconnect() {
    try {
      return Boolean(await googleToken(false));
    } catch {
      return false;
    }
  },
  // Kept so screens.js need not know the difference: with the backend as the
  // source of truth there is nothing local to remember.
  remember: () => {},
  get remembered() {
    return true;
  },
  forget: () => googleTokens.clear(),
  revoke() {
    // Revoked at Google and deleted from the backend, not merely dropped
    // here. A disconnect that only forgot locally would leave a live
    // credential to a year of somebody's calendar with nothing pointing at
    // it, which is worse than never having had the feature.
    googleTokens.clear();
    disconnectGoogle(config, session).catch(() => {});
  },
};

async function boot() {
  try {
    const response = await fetch('./config.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`config.json returned ${response.status}`);
    config = await response.json();
  } catch {
    ui.showLoginError('Could not load configuration. Reload the page.');
    return;
  }
  bind();

  // The tokens have been in localStorage all along -- amazon-cognito-identity-js
  // puts them there -- and this page never looked, which is the whole reason
  // the form appeared every morning. The refresh token is good for 30 days, so
  // ordinary daily use never sees it again. A failed restore is not an error
  // worth saying out loud: it is a login screen.
  const restored = await restoreSession(config);
  if (restored) enterApp(restored, restored.username);
  else ui.showLogin();
}

/**
 * Everything that has to be true once there is a session, whether it came from
 * the form or from the last time this device signed in.
 *
 * One function rather than two so the restored path cannot quietly drift from
 * the typed one -- a sidebar that only mounts after a manual sign-in is the
 * kind of difference nobody notices until the morning it matters.
 */
function enterApp(newSession, user) {
  session = newSession;
  username = user;
  ui.showApp();
  // Google is a separate consent and is not granted by signing in here. All
  // this hands over is the ability to ask for one.
  screens.setCalendarAccess(calendarAccess);
  // Where a finished practice test goes. Handed over rather than reached
  // for, so `screens.js` never has to know about the API or the session --
  // the same seam `setCalendarAccess` is. Before it is wired the screen
  // says "not sent" rather than pretending, which matters: a submission
  // that silently failed would cost the whole point of the feature.
  screens.useQuizSender(
    (attempt) => sessions.submitAttempt(config, newSession, attempt));
  timerscreen.setSessionSource(config, newSession);
  // Both halves of the forgotten-timer rule meet here: the server capped it,
  // and this is the ask about when it really ended. Not awaited -- a timer
  // question must not hold up the screen this site exists for.
  timerscreen.askAboutForgotten();
  // After the app is up, not before. Canvas being down must not hold the
  // sign-in open or cost you the upload page, which is what this site is
  // for and which works perfectly well without Canvas.
  refreshClasses();
  // The same rule, for the same reason: the history is worth having and it is
  // not worth holding the screen for. Not awaited.
  refreshCaptures();
}

/**
 * Fill the history from the API.
 *
 * The list used to be built by `ui.addCapture` from uploads made in the open
 * tab, so a reload said "Nothing uploaded yet" however many recordings had
 * gone up. Allen, 2026-08-29: "make it show all the files that have been
 * uploaded, in some kind of history view."
 *
 * A failure says so in the panel rather than leaving the empty line standing.
 * "Nothing uploaded yet" and "this could not be read" are the same list to
 * look at and two completely different things to do about, which is the same
 * distinction `refreshClasses` draws for Canvas.
 */
async function refreshCaptures() {
  try {
    ui.showCaptures(await listMemos(config, session));
  } catch (err) {
    ui.captureTrouble(err?.message || 'Could not reach your uploads.');
  }
}

function bind() {
  ui.el('login-form').addEventListener('submit', onSignIn);
  ui.el('sign-out').addEventListener('click', onSignOut);
  ui.el('upload-button').addEventListener('click', openClassPick);
  ui.el('class-pick-cancel').addEventListener('click', closeClassPick);
  ui.el('class-pick-list').addEventListener('click', onClassPicked);
  ui.el('file-input').addEventListener('change', onFileChosen);
  ui.el('note-open').addEventListener('click', ui.showNote);
  ui.el('note-back').addEventListener('click', ui.showHome);

  // Delegated: the class rows do not exist until mountSidebar runs.
  ui.el('side-nav').addEventListener('click', onNavClick);

  ui.el('study-back').addEventListener('click', screens.backToClass);
  ui.el('done-back').addEventListener('click', screens.backToClass);
  ui.el('study-shuffle').addEventListener('click', screens.shuffle);
  ui.el('do-flip').addEventListener('click', screens.flip);
  ui.el('mark-known').addEventListener('click', screens.markKnown);
  ui.el('mark-forgot').addEventListener('click', screens.markForgot);
  ui.el('review-forgot').addEventListener('click', screens.reviewForgot);
  ui.el('restart-set').addEventListener('click', screens.restart);

  // The practice test screen. Its controls are deliberately in two groups:
  // the answers, and the two that are not answers -- skip and "I don't
  // know" -- because they mean opposite things about what he needs to
  // study and a row of six identical buttons would hide that.
  ui.el('quiz-back').addEventListener('click', screens.backToClassFromTest);
  ui.el('quiz-prev').addEventListener('click', screens.quizPrevious);
  ui.el('quiz-next').addEventListener('click', screens.quizNext);
  ui.el('quiz-skip').addEventListener('click', screens.quizSkip);
  ui.el('quiz-unknown').addEventListener('click', screens.quizDontKnow);
  ui.el('quiz-submit').addEventListener('click', screens.quizSubmit);
  ui.el('quiz-review').addEventListener('click', screens.quizReview);
  ui.el('quiz-resend').addEventListener('click', screens.quizResend);

  ui.el('notepage-back').addEventListener('click', screens.backToClassFromNote);
  ui.el('notepage-title').addEventListener('input', screens.onNoteEdited);
  ui.el('notepage-body').addEventListener('input', screens.onNoteEdited);

  const stage = ui.el('card-stage');
  stage.addEventListener('pointerdown', screens.onCardPointerDown);
  stage.addEventListener('pointermove', screens.onCardPointerMove);
  stage.addEventListener('pointerup', screens.onCardPointerUp);
  stage.addEventListener('pointercancel', screens.onCardPointerCancel);
  stage.addEventListener('keydown', screens.onCardKeyDown);

  // The session timer. The pill and the back button only ever navigate; the
  // two that can change a session are on the timer screen itself, where the
  // clock is in front of you.
  ui.el('cal-unscheduled').addEventListener('click', timerscreen.openUnscheduled);
  ui.el('unscheduled-form').addEventListener('submit', timerscreen.submitUnscheduled);
  ui.el('unscheduled-cancel').addEventListener('click', timerscreen.cancelUnscheduled);
  ui.el('home-timer').addEventListener('click', timerscreen.openTimer);
  ui.el('timer-back').addEventListener('click', timerscreen.leaveTimer);
  ui.el('timer-break').addEventListener('click', timerscreen.takeABreak);
  ui.el('timer-pause').addEventListener('click', timerscreen.toggleTimerPause);
  ui.el('timer-finish').addEventListener('click', timerscreen.finishTimer);
  ui.el('timer-cancel').addEventListener('click', timerscreen.cancelTimer);
  ui.el('canvas-done-keep').addEventListener('click', screens.keepCanvasDone);
  ui.el('canvas-done-untick').addEventListener('click', screens.untickCanvasDoneAnyway);
  ui.el('forgotten-answer').addEventListener('click', timerscreen.answerForgotten);
  ui.el('forgotten-dismiss').addEventListener('click', timerscreen.dismissForgotten);

  ui.el('cal-connect-btn').addEventListener('click', screens.connectCalendar);
  ui.el('cal-disconnect').addEventListener('click', screens.disconnectCalendar);
  ui.el('cal-views').addEventListener('click', (event) => {
    const button = event.target.closest('.seg-btn');
    if (button) screens.setCalendarView(button.dataset.view);
  });
  // Delegated, matching cal-views above: the chips are rebuilt on every
  // refresh, so a listener bound to one would be listening to nothing after
  // the next fetch.
  ui.el('cal-sources').addEventListener('click', screens.onCalendarSourceTap);

  // Delegated: agenda rows are rebuilt on every Canvas refresh.
  ui.el('agenda-days').addEventListener('click', timerscreen.startForAssignment);
  // A second delegated listener on the same container, not a branch inside
  // the first: `.agenda-expand` is a sibling of `.agenda-start`, so a tap on
  // it never matches `startForAssignment`'s own `.closest('.agenda-start')`
  // and this one is a no-op for every tap that isn't the chevron.
  ui.el('agenda-days').addEventListener('click', screens.onAgendaExpandTap);
  // Delegated, because the rows are replaced on every refresh and a listener
  // bound to a row would be listening to an element nobody can see any more.
  ui.el('today-list').addEventListener('click', screens.onTodayCheck);
  // Delegated for the same reason: the calendar's rows are replaced on every
  // refresh, and only Alter's own study blocks carry a button at all.
  ui.el('cal-days').addEventListener('click', screens.onCalendarBlockTap);
  // Delegated on the list, not on each row: the study panel is rebuilt on
  // every home refresh and a listener bound to a row would go with it.
  ui.el('study-list').addEventListener('click', screens.onStudyRowTap);
  ui.el('timer-start').addEventListener('click', timerscreen.startChosen);
  // A timer ending ticks its item off, and today's list has to be told so it
  // can redraw. The hook rather than an import: timerscreen is imported BY
  // screens, and importing it back would be a cycle.
  timerscreen.setSessionEndedHook(() => screens.showToday());

  ui.el('agenda-views').addEventListener('click', (event) => {
    const button = event.target.closest('.seg-btn');
    if (button) screens.showAgenda(button.dataset.view);
  });

  screens.mountSidebar();
  // A sidebar that fits at one width clips at another, and the class names
  // are the only thing on the page whose overflow is not obvious.
  window.addEventListener('resize', screens.measureLabels);
}

function onNavClick(event) {
  const row = event.target.closest('.nav-row');
  if (!row) return;

  const target = row.dataset.nav;
  if (target === 'home') ui.showHome();
  else if (target === 'note') ui.showNote();
  else if (target === 'calendar') screens.openCalendar();
  else if (target.startsWith('class-')) screens.openClass(Number(target.slice(6)));
}

/**
 * The class is chosen before the file, because it is the one thing nothing
 * downstream can recover. No classes means no upload: a recording that
 * belongs to nothing has no folder to land in and nothing to be asked about.
 */
function openClassPick() {
  if (CLASSES.length === 0) {
    ui.setStatus('No Canvas classes yet, so there is nowhere to file a recording.', 'error');
    return;
  }

  const list = ui.el('class-pick-list');
  list.replaceChildren();

  CLASSES.forEach((cls, index) => {
    const row = ui.clone('tpl-class-pick');
    row.dataset.class = String(index);
    row.querySelector('.nav-dot').style.background = cls.color;
    row.querySelector('.class-pick-name').textContent = cls.name;
    list.append(row);
  });

  ui.el('class-pick').hidden = false;
}

function closeClassPick() {
  ui.el('class-pick').hidden = true;
}

function onClassPicked(event) {
  const row = event.target.closest('.class-pick-row');
  if (!row) return;
  pendingCourse = CLASSES[Number(row.dataset.class)];
  if (!pendingCourse) return;
  closeClassPick();
  ui.el('file-input').click();
}

async function onSignIn(event) {
  event.preventDefault();
  const submit = ui.el('login-submit');
  const user = ui.el('login-username').value.trim();
  const pass = ui.el('login-password').value;

  if (!user || !pass) {
    ui.showLoginError('Enter your username and password.');
    return;
  }

  ui.showLoginError('');
  submit.disabled = true;
  submit.textContent = 'Signing in…';

  try {
    const signedIn = await signIn(config, user, pass);
    ui.el('login-password').value = '';
    enterApp(signedIn, user);
  } catch (err) {
    ui.showLoginError(err instanceof AuthError ? err.friendly : 'Could not sign in.');
  } finally {
    submit.disabled = false;
    submit.textContent = 'Sign in';
  }
}

/**
 * Fill the sidebar from Canvas.
 *
 * Deliberately not awaited by the caller and deliberately unable to throw: a
 * Canvas outage is a smaller thing than being signed out, and the recording
 * upload does not need Canvas at all.
 */
async function refreshClasses() {
  const result = await loadClasses(config, session);
  screens.mountSidebar();
  if (!result.ok || result.count === 0) {
    // Said once, on the screen they are already looking at. An expired token
    // carries no retry, because the fix is on a machine that is not this one.
    ui.setStatus(result.message, result.ok ? null : 'error');
  }

  // Joins on course code, so it has to wait for the classes above to exist.
  //
  // A count of zero is still not surfaced: a class with no material yet
  // already reads as empty on its own screen, the same honest empty state
  // classes.js gives it before Canvas answers, and that must not overwrite
  // whatever loadClasses just said about Canvas itself.
  //
  // A *failure* is surfaced (2026-08-13). Those two were treated alike, and
  // they are not alike: "nothing has been made yet" and "your material is
  // there and this page could not read it" looked identical on screen, so a
  // published set that never loaded was indistinguishable from one that was
  // never made. Only shown when `loadClasses` had nothing of its own to say,
  // so the Canvas message still wins the one status line.
  const study = await loadStudy(config, session, CLASSES);
  if (!study.ok && result.ok && result.count > 0) {
    ui.setStatus(study.message, 'error');
  }

  // The agenda joins course ids to the sidebar's colours, so it is drawn
  // after the classes land rather than racing them.
  screens.setAgendaSource(config, session);
  screens.showAgenda();

  // Today's list reads the same Canvas reply the agenda does, plus the plan
  // the desktop published and the calendar. Drawn after the classes for the
  // same reason as the agenda: it shows course codes, and racing them would
  // show a list that fills in its own labels a moment later.
  screens.setTodaySource(config, session);
  screens.showToday();
}

function onSignOut() {
  signOut(config, username);
  replaceClasses([]);
  // Signing out of Alter hands the Google token back to Google as well. The
  // two grants are separate, but leaving one alive on a machine somebody just
  // signed out of would make them separate in the wrong direction.
  calendarAccess.revoke();
  screens.setCalendarAccess(null);
  timerscreen.setSessionSource(null, null);
  screens.setAgendaSource(null, null);
  screens.setTodaySource(null, null);
  screens.mountSidebar();
  session = null;
  username = null;
  // The class-pick panel lives inside home-view, so showLogin() below hides
  // its ancestor but leaves it unhidden underneath -- a re-sign-in would put
  // it straight back on screen holding rows indexed into the class list this
  // user is about to replace. A recording filed against a stale index cannot
  // be recovered downstream, so it is closed here rather than left to chance.
  closeClassPick();
  pendingCourse = null;
  ui.setStatus('', null);
  ui.setProgress(null);
  ui.showLogin();
}

async function onFileChosen(event) {
  const file = event.target.files?.[0];
  event.target.value = ''; // so re-picking the same file fires change again
  if (!file) return;

  const course = pendingCourse;
  pendingCourse = null;
  if (!course) return; // the dialog was opened some other way

  const check = validateAudioFile(file);
  if (!check.ok) {
    ui.setStatus(check.reason, 'error');
    return;
  }

  ui.el('upload-button').disabled = true;
  ui.setStatus(`Uploading ${file.name}…`, null);
  ui.setProgress(0);

  try {
    const key = await uploadMemo({
      config,
      session,
      file,
      course,
      onProgress: (pct) => ui.setProgress(pct),
    });
    ui.setProgress(100);
    ui.setStatus(`Uploaded ${file.name} for ${course.meta || course.name}.`, 'done');
    // On screen straight away, from what this page already knows, so the row
    // does not wait on a round trip to appear. The record below is what makes
    // it survive a reload.
    ui.addCapture({
      key,
      name: file.name,
      course: course.meta || course.name,
      courseName: course.name,
      size: file.size,
      uploadedAt: Date.now() / 1000,
    });
    // After the transfer resolved, never before, so the history cannot list a
    // file that did not arrive. Guarded on its own: the upload succeeded, and
    // failing to write it down must not report the upload itself as failed.
    try {
      await recordMemo(config, session, { key, file, course });
    } catch {
      // Deliberately silent. The row is on screen, the recording is in the
      // bucket and the worker will transcribe it either way; the only thing
      // lost is that this one upload may be missing from the list tomorrow,
      // and a red line about it would say the upload failed, which is false.
    }
  } catch (err) {
    ui.setStatus(describeUploadError(err), 'error');
  } finally {
    ui.el('upload-button').disabled = false;
    setTimeout(() => ui.setProgress(null), 1200);
  }
}

boot();
